import { useState } from 'react';

interface ShareButtonsProps {
  title: string;
  url?: string;
}

export default function ShareButtons({ title, url }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url ?? window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const buttons = [
    {
      label: 'Share on X',
      icon: 'ri-twitter-x-line',
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: 'hover:bg-stone-900 hover:text-white hover:border-stone-900',
    },
    {
      label: 'Pin on Pinterest',
      icon: 'ri-pinterest-line',
      href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`,
      color: 'hover:bg-red-600 hover:text-white hover:border-red-600',
    },
    {
      label: 'Share on Facebook',
      icon: 'ri-facebook-line',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'hover:bg-blue-600 hover:text-white hover:border-blue-600',
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-stone-100">
      <span className="text-xs font-semibold text-stone-400 uppercase tracking-widest mr-1 whitespace-nowrap">
        Share
      </span>

      {buttons.map(({ label, icon, href, color }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="nofollow noopener noreferrer"
          aria-label={label}
          title={label}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 text-stone-500 text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${color}`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${icon} text-sm`} />
          </div>
          <span className="hidden sm:inline">{label.replace('Share on ', '').replace('Pin on ', '')}</span>
        </a>
      ))}

      {/* Copy link */}
      <button
        onClick={handleCopy}
        aria-label="Copy link"
        title="Copy link"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
          copied
            ? 'bg-rose-500 text-white border-rose-500'
            : 'border-stone-200 text-stone-500 hover:bg-rose-500 hover:text-white hover:border-rose-500'
        }`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-sm`} />
        </div>
        <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy link'}</span>
      </button>
    </div>
  );
}
